/**
 * Cliente HTTP centralizado para la nueva API Backend
 * Maneja autenticación, interceptors, errores y logging
 */

import { useUser } from '@clerk/clerk-react';
import { 
  API_BASE_URL, 
  DEFAULT_HEADERS, 
  API_TIMEOUT, 
  HTTP_STATUS, 
  RETRY_CONFIG,
  LOG_CONFIG 
} from '../config/api';
import { 
  getClerkToken, 
  setupClerkInterceptors, 
  validateToken, 
  refreshToken 
} from '../utils/clerkUtils';
import { handleError, createErrorInterceptor } from './errorHandler';

/**
 * Clase principal del cliente API
 */
class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.timeout = API_TIMEOUT;
    this.defaultHeaders = { ...DEFAULT_HEADERS };
    
    // Interceptors
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    this.errorInterceptors = [];
    
    // Configurar interceptors por defecto
    this.setupDefaultInterceptors();
  }

  /**
   * Configura interceptors por defecto
   */
  setupDefaultInterceptors() {
    // Request interceptor para autenticación automática
    this.addRequestInterceptor(async (config) => {
      try {
        const token = await getClerkToken();
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('[ApiClient] Error al obtener token para request:', error);
      }
      return config;
    });

    // Request interceptor para logging
    this.addRequestInterceptor((config) => {
      if (LOG_CONFIG.ENABLED && LOG_CONFIG.LOG_REQUESTS) {
        console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
          headers: { ...config.headers, Authorization: config.headers?.Authorization ? '[REDACTED]' : undefined },
          data: config.data
        });
      }
      return config;
    });

    // Response interceptor para logging
    this.addResponseInterceptor((response) => {
      if (LOG_CONFIG.ENABLED && LOG_CONFIG.LOG_RESPONSES) {
        console.log(`[API Response] ${response.status} ${response.config?.url}`, {
          data: response.data,
          headers: response.headers
        });
      }
      return response;
    });

    // Error interceptor para manejo de errores 401 (token inválido/expirado)
    this.addErrorInterceptor(async (error) => {
      if (error.response?.status === 401) {
        console.warn('[ApiClient] Token inválido o expirado, intentando renovar...');
        
        try {
          const newToken = await refreshToken();
          if (newToken) {
            //console.log('[ApiClient] Token renovado exitosamente');
            
            // Reintentar la request original con el nuevo token
            if (error.config) {
              error.config.headers = error.config.headers || {};
              error.config.headers.Authorization = `Bearer ${newToken}`;
              
              // Hacer la request nuevamente
              return this.request(error.config);
            }
          } else {
            console.error('[ApiClient] No se pudo renovar el token, cerrando sesión');
            
            // Cerrar sesión si no se puede renovar el token
            if (typeof window !== 'undefined' && window.Clerk?.signOut) {
              await window.Clerk.signOut();
            }
            
            // Redirigir usando Clerk si estamos en el browser
            if (typeof window !== 'undefined') {
              const currentPath = window.location.pathname;
              // No redirigir si ya estamos en el landing (donde está el login de Clerk)
              if (currentPath !== '/' && currentPath !== '/landing') {
                // Usar Clerk para redirigir al sign-in
                if (window.Clerk?.redirectToSignIn) {
                  window.Clerk.redirectToSignIn();
                } else {
                  // Fallback: redirigir al landing donde está el SignInButton
                  window.location.href = '/';
                }
              }
            }
          }
        } catch (refreshError) {
          console.error('[ApiClient] Error al renovar token:', refreshError);
        }
      }
      
      return Promise.reject(error);
    });

    // Error interceptor para logging y manejo centralizado
    this.addErrorInterceptor((error) => {
      if (LOG_CONFIG.ENABLED && LOG_CONFIG.LOG_ERRORS) {
        console.error(`[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        });
      }
      
      // Usar el error handler centralizado
      try {
        handleError(error, { showToast: true, logError: false, throwError: false });
      } catch (handlerError) {
        console.error('[ApiClient] Error en error handler:', handlerError);
      }
      
      return Promise.reject(error);
    });
  }

  /**
   * Añade interceptor de request
   */
  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Añade interceptor de response
   */
  addResponseInterceptor(interceptor) {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Añade interceptor de error
   */
  addErrorInterceptor(interceptor) {
    this.errorInterceptors.push(interceptor);
  }

  /**
   * Obtiene token de Clerk (delegado a la utility)
   */
  async getClerkToken() {
    return await getClerkToken();
  }

  /**
   * Ejecuta interceptors de request
   */
  async executeRequestInterceptors(config) {
    let modifiedConfig = { ...config };
    
    for (const interceptor of this.requestInterceptors) {
      try {
        modifiedConfig = await interceptor(modifiedConfig) || modifiedConfig;
      } catch (error) {
        console.error('[ApiClient] Error en request interceptor:', error);
      }
    }
    
    return modifiedConfig;
  }

  /**
   * Ejecuta interceptors de response
   */
  async executeResponseInterceptors(response) {
    let modifiedResponse = response;
    
    for (const interceptor of this.responseInterceptors) {
      try {
        modifiedResponse = await interceptor(modifiedResponse) || modifiedResponse;
      } catch (error) {
        console.error('[ApiClient] Error en response interceptor:', error);
      }
    }
    
    return modifiedResponse;
  }

  /**
   * Ejecuta interceptors de error
   */
  async executeErrorInterceptors(error) {
    for (const interceptor of this.errorInterceptors) {
      try {
        await interceptor(error);
      } catch (interceptorError) {
        console.error('[ApiClient] Error en error interceptor:', interceptorError);
      }
    }
  }

  /**
   * Construye URL completa
   */
  buildUrl(endpoint) {
    if (endpoint.startsWith('http')) {
      return endpoint;
    }
    return `${this.baseURL}${endpoint}`;
  }

  /**
   * Prepara headers de la request
   */
  async prepareHeaders(customHeaders = {}) {
    const headers = { ...this.defaultHeaders, ...customHeaders };
    
    // Añadir token de autorización si está disponible
    const token = await this.getClerkToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * Maneja retry logic
   */
  async executeWithRetry(requestFn, retries = RETRY_CONFIG.MAX_RETRIES) {
    try {
      return await requestFn();
    } catch (error) {
      const shouldRetry = retries > 0 && 
        RETRY_CONFIG.RETRY_STATUS_CODES.includes(error.response?.status);
      
      if (shouldRetry) {
        //console.log(`[ApiClient] Reintentando request. Intentos restantes: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, RETRY_CONFIG.RETRY_DELAY));
        return this.executeWithRetry(requestFn, retries - 1);
      }
      
      throw error;
    }
  }

  /**
   * Método principal para hacer requests
   */
  async request(config) {
    try {
      // Preparar configuración
      const requestConfig = {
        method: 'GET',
        ...config,
        url: this.buildUrl(config.url || config.endpoint),
        headers: await this.prepareHeaders(config.headers),
        timeout: config.timeout || this.timeout
      };

      // Ejecutar interceptors de request
      const finalConfig = await this.executeRequestInterceptors(requestConfig);

      // Función que ejecuta el fetch
      const executeFetch = async () => {
        // Preparar body según el tipo de datos
        let body;
        let headers = { ...finalConfig.headers };
        
        if (finalConfig.data) {
          if (finalConfig.data instanceof FormData) {
            // Para FormData, enviar directamente sin modificar y remover Content-Type
            body = finalConfig.data;
            // Eliminar Content-Type para que el browser lo configure automáticamente
            delete headers['Content-Type'];
            delete headers['content-type'];
          } else {
            // Para otros datos, convertir a JSON
            body = JSON.stringify(finalConfig.data);
          }
        }

        const response = await fetch(finalConfig.url, {
          method: finalConfig.method,
          headers: headers,
          body: body,
          signal: AbortSignal.timeout(finalConfig.timeout)
        });

        // Parsear respuesta
        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          data = await response.text();
        }

        // Crear objeto de respuesta similar a axios
        const responseObj = {
          data,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          config: finalConfig
        };

        // Verificar si la respuesta es exitosa
        if (!response.ok) {
          const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
          error.response = responseObj;
          error.config = finalConfig;
          throw error;
        }

        return responseObj;
      };

      // Ejecutar con retry (excepto para FormData que puede causar duplicación)
      const isFormData = finalConfig.data instanceof FormData;
      const response = isFormData 
        ? await executeFetch() // Sin retry para FormData
        : await this.executeWithRetry(executeFetch); // Con retry para otros requests

      // Ejecutar interceptors de response
      const finalResponse = await this.executeResponseInterceptors(response);

      return finalResponse;

    } catch (error) {
      // Ejecutar interceptors de error
      await this.executeErrorInterceptors(error);
      throw error;
    }
  }

  /**
   * Métodos de conveniencia HTTP
   */
  async get(url, config = {}) {
    return this.request({ ...config, method: 'GET', url });
  }

  async post(url, data, config = {}) {
    return this.request({ ...config, method: 'POST', url, data });
  }

  async put(url, data, config = {}) {
    return this.request({ ...config, method: 'PUT', url, data });
  }

  async patch(url, data, config = {}) {
    return this.request({ ...config, method: 'PATCH', url, data });
  }

  async delete(url, config = {}) {
    return this.request({ ...config, method: 'DELETE', url });
  }
}

// Instancia singleton del cliente
const apiClient = new ApiClient();

export default apiClient;

/**
 * Hook para usar el cliente API en componentes React
 */
export const useApiClient = () => {
  const { user } = useUser();
  
  // Actualizar referencia al usuario de Clerk
  if (typeof window !== 'undefined' && user) {
    window.__CLERK_USER__ = user;
  }
  
  return apiClient;
};

/**
 * Funciones helper para casos comunes
 */
export const createApiError = (message, status, data = null) => {
  const error = new Error(message);
  error.status = status;
  error.data = data;
  return error;
};

export const isApiError = (error) => {
  return error && typeof error.status === 'number';
};

export const getErrorMessage = (error) => {
  if (isApiError(error)) {
    return error.response?.data?.message || error.message || 'Error en la API';
  }
  return error.message || 'Error desconocido';
};